import React, { useState, useEffect } from "react";
import { API_URL } from "../../constant.js";
import "./ReferralHistory.css";
import { useNavigate, useLocation } from "react-router-dom";

function ReferralHistory() {
  const [agentCurrentPage, setAgentCurrentPage] = useState(1);
  const [businessCurrentPage, setBusinessCurrentPage] = useState(1);
  const [agentReferrals, setAgentReferrals] = useState([]);
  const [businessReferrals, setBusinessReferrals] = useState([]);
  const [loading, setLoading] = useState({ agents: true, businesses: true });
  const [error, setError] = useState({ agents: "", businesses: "" });
  const [activeTab, setActiveTab] = useState("agents");
  const [agentTotalPages, setAgentTotalPages] = useState(1);
  const [businessTotalPages, setBusinessTotalPages] = useState(1);
  const [agentTotalItems, setAgentTotalItems] = useState(0);
  const [businessTotalItems, setBusinessTotalItems] = useState(0);
  const [agentSummary, setAgentSummary] = useState({
    total_referrals: 0,
    verified_agents: 0,
    pending_verification: 0,
  });
  const [businessSummary, setBusinessSummary] = useState({
    total_referrals: 0,
    approved_businesses: 0,
    pending_approval: 0,
  });
  const [userRole, setUserRole] = useState(null);
  const [containerStyle, setContainerStyle] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for tab parameter in URL
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");

    if (tabParam === "agents" || tabParam === "businesses") {
      setActiveTab(tabParam);

      // Fetch data for the specified tab
      if (tabParam === "agents") {
        fetchAgentReferralHistory(1);
      } else {
        fetchBusinessReferralHistory(1);
      }
    } else {
      // Default behavior - fetch both
      fetchAgentReferralHistory(1);
      fetchBusinessReferralHistory(1);
    }

    getUserRole();
  }, [location.search]);

  // Set container style based on user role
  const updateContainerStyle = (role) => {
    let background = "";

    switch (role) {
      case "SeniorBusinessAdsAgent":
        background = "linear-gradient(to bottom, #024225, white)";
        break;
      case "MiniBusinessAdsAgent":
        background = "linear-gradient(to bottom, #09450b, white)";
        break;
      default:
        background = "linear-gradient(to bottom, #024225, white)";
        break;
    }

    setContainerStyle({
      display: "flex",
      flexDirection: "column",
      marginTop: "0px",
      marginBottom: "0px",
      background: background,
      width: "100%",
      minHeight: "100vh",
      fontFamily: "'Poppins', sans-serif",
      padding: "20px",
    });
  };

  // Get user role
  const getUserRole = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        navigate("/retail-agent-login");
        return;
      }

      const response = await fetch(`${API_URL}/api/agent/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserRole(data.user.role);
        updateContainerStyle(data.user.role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  // Fetch agent referral data
  const fetchAgentReferralHistory = async (page = 1) => {
    try {
      setLoading((prev) => ({ ...prev, agents: true }));
      const token = localStorage.getItem("auth_token");

      const response = await fetch(
        `${API_URL}/api/agent/referrals/agents?page=${page}&per_page=6`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch agent referral history");
      }

      const data = await response.json();

      if (data.success) {
        setAgentReferrals(data.data.referred_agents || []);
        setAgentSummary({
          total_referrals: data.data.summary?.total_referred_agents || 0,
          verified_agents: data.data.summary?.verified_agents || 0,
          pending_verification: data.data.summary?.pending_verification || 0,
        });
        setAgentTotalPages(data.data.pagination?.last_page || 1);
        setAgentTotalItems(data.data.pagination?.total || 0);
        setError((prev) => ({ ...prev, agents: "" }));
      } else {
        throw new Error(
          data.message || "Failed to load agent referral history"
        );
      }
    } catch (err) {
      setError((prev) => ({ ...prev, agents: err.message }));
      console.error("Error fetching agent referral history:", err);
    } finally {
      setLoading((prev) => ({ ...prev, agents: false }));
    }
  };

  // Fetch business referral data
  const fetchBusinessReferralHistory = async (page = 1) => {
    try {
      setLoading((prev) => ({ ...prev, businesses: true }));
      const token = localStorage.getItem("auth_token");

      const response = await fetch(
        `${API_URL}/api/agent/referrals/businesses?page=${page}&per_page=6`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch business referral history");
      }

      const data = await response.json();

      if (data.success) {
        setBusinessReferrals(data.data.referred_businesses || []);
        setBusinessSummary({
          total_referrals: data.data.summary?.total_referred_businesses || 0,
          approved_businesses: data.data.summary?.approved_businesses || 0,
          pending_approval: data.data.summary?.pending_approval || 0,
        });
        setBusinessTotalPages(data.data.pagination?.last_page || 1);
        setBusinessTotalItems(data.data.pagination?.total || 0);
        setError((prev) => ({ ...prev, businesses: "" }));
      } else {
        throw new Error(
          data.message || "Failed to load business referral history"
        );
      }
    } catch (err) {
      setError((prev) => ({ ...prev, businesses: err.message }));
      console.error("Error fetching business referral history:", err);
    } finally {
      setLoading((prev) => ({ ...prev, businesses: false }));
    }
  };

  useEffect(() => {
    getUserRole();
    fetchAgentReferralHistory(1);
    fetchBusinessReferralHistory(1);
  }, []);

  const handlePageChange = (newPage) => {
    if (activeTab === "agents") {
      setAgentCurrentPage(newPage);
      fetchAgentReferralHistory(newPage);
    } else {
      setBusinessCurrentPage(newPage);
      fetchBusinessReferralHistory(newPage);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Update URL with tab parameter without page reload
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);
    navigate(`?${searchParams.toString()}`, { replace: true });
    
    // Fetch data for the selected tab
    if (tab === 'agents') {
      fetchAgentReferralHistory(1);
      setAgentCurrentPage(1);
    } else {
      fetchBusinessReferralHistory(1);
      setBusinessCurrentPage(1);
    }
  };

  const getCurrentPage = () => {
    return activeTab === "agents" ? agentCurrentPage : businessCurrentPage;
  };

  const getCurrentReferrals = () => {
    return activeTab === "agents" ? agentReferrals : businessReferrals;
  };

  const getCurrentSummary = () => {
    return activeTab === "agents" ? agentSummary : businessSummary;
  };

  const getTotalPages = () => {
    return activeTab === "agents" ? agentTotalPages : businessTotalPages;
  };

  const getTotalItems = () => {
    return activeTab === "agents" ? agentTotalItems : businessTotalItems;
  };

  const getCurrentLoading = () => {
    return activeTab === "agents" ? loading.agents : loading.businesses;
  };

  const getCurrentError = () => {
    return activeTab === "agents" ? error.agents : error.businesses;
  };

  const formatLocation = (referral) => {
    if (referral.country && referral.region) {
      return `${referral.country}, ${referral.region}`;
    } else if (referral.country) {
      return referral.country;
    } else if (referral.region) {
      return referral.region;
    }
    return "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStatusText = (status) => {
    if (activeTab === "agents") {
      return status === "verified" ? "Active" : status || "Pending";
    } else {
      return status === "approved" ? "Active" : status || "Pending";
    }
  };

  const getStatusClass = (status) => {
    if (activeTab === "agents") {
      return status === "verified" ? "active" : "pending";
    } else {
      return status === "approved" ? "active" : "pending";
    }
  };

  const LoadingIcon = () => (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>
        Loading {activeTab === "agents" ? "agent" : "business"} referral
        history...
      </p>
    </div>
  );

  const handleRetry = () => {
    const currentPage = getCurrentPage();
    if (activeTab === "agents") {
      fetchAgentReferralHistory(currentPage);
    } else {
      fetchBusinessReferralHistory(currentPage);
    }
  };

  // Generate pagination page numbers
  const generatePageNumbers = () => {
    const currentPage = getCurrentPage();
    const totalPages = getTotalPages();
    const pageNumbers = [];

    if (totalPages <= 5) {
      // Show all pages if total pages is 5 or less
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      // Calculate start and end of middle pages
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= 3) {
        end = 4;
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      // Add ellipsis after first page if needed
      if (start > 2) {
        pageNumbers.push("...");
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pageNumbers.push("...");
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <div style={containerStyle}>
      <div className="referral-history-container">
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <div className="tab-buttons">
            <button
              className={`tab-button ${activeTab === "agents" ? "active" : ""}`}
              onClick={() => handleTabChange("agents")}
            >
              <span className="tab-label">Referred Agents</span>
              {agentSummary.total_referrals > 0 && (
                <span className="tab-count">
                  {agentSummary.total_referrals}
                </span>
              )}
            </button>
            <button
              className={`tab-button ${
                activeTab === "businesses" ? "active" : ""
              }`}
              onClick={() => handleTabChange("businesses")}
            >
              <span className="tab-label">Referred Businesses</span>
              {businessSummary.total_referrals > 0 && (
                <span className="tab-count">
                  {businessSummary.total_referrals}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="table-container1">
          {getCurrentLoading() ? (
            <LoadingIcon />
          ) : getCurrentError() ? (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <h3>Error Loading Data</h3>
              <p>{getCurrentError()}</p>
              <button className="retry-btn" onClick={handleRetry}>
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="referral-table">
                  <thead>
                    <tr>
                      <th style={{ paddingLeft: "30px" }}>No</th>
                      <th>
                        {activeTab === "agents"
                          ? "Agent Name"
                          : "Business Name"}
                      </th>
                      <th>Email</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Joined Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getCurrentReferrals().length > 0 ? (
                      getCurrentReferrals().map((referral, index) => {
                        const currentPage = getCurrentPage();
                        const itemsPerPage = 6;
                        const itemNumber =
                          (currentPage - 1) * itemsPerPage + index + 1;

                        return (
                          <tr key={referral.id || index}>
                            <td>{itemNumber}</td>
                            <td className="name-cell">
                              {activeTab === "agents"
                                ? referral.name ||
                                  `${referral.first_name || ""} ${
                                    referral.last_name || ""
                                  }`.trim()
                                : referral.business_name ||
                                  referral.name ||
                                  "N/A"}
                            </td>
                            <td>
                              {activeTab === "agents"
                                ? referral.email || "N/A"
                                : referral.email || "N/A"}
                            </td>
                            <td>{formatLocation(referral)}</td>
                            <td>
                              <span
                                className={`status-badge ${getStatusClass(
                                  referral.status
                                )}`}
                              >
                                {getStatusText(referral.status)}
                              </span>
                            </td>
                            <td>
                              {formatDate(
                                referral.join_date ||
                                  referral.registration_date ||
                                  referral.created_at
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6">
                          <div className="empty-state">
                            <div className="empty-icon">
                              {activeTab === "agents" ? "👤" : "🏢"}
                            </div>
                            <h3>
                              No{" "}
                              {activeTab === "agents" ? "Agents" : "Businesses"}{" "}
                              Found
                            </h3>
                            <p>
                              You haven't referred any{" "}
                              {activeTab === "agents" ? "agents" : "businesses"}{" "}
                              yet.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {getTotalPages() > 1 && getCurrentReferrals().length > 0 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {(getCurrentPage() - 1) * 6 + 1} to{" "}
                    {Math.min(getCurrentPage() * 6, getTotalItems())} of{" "}
                    {getTotalItems()} entries
                  </div>
                  <div className="pagination">
                    <button
                      className="page-btn prev-next first-page"
                      onClick={() => handlePageChange(1)}
                      disabled={getCurrentPage() === 1}
                      title="First Page"
                    >
                      «
                    </button>
                    <button
                      className="page-btn prev-next"
                      onClick={() => handlePageChange(getCurrentPage() - 1)}
                      disabled={getCurrentPage() === 1}
                    >
                      ← Previous
                    </button>

                    <div className="page-numbers">
                      {generatePageNumbers().map((pageNum, index) => {
                        if (pageNum === "...") {
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              className="page-ellipsis"
                            >
                              ...
                            </span>
                          );
                        }

                        return (
                          <button
                            key={pageNum}
                            className={`page-btn ${
                              getCurrentPage() === pageNum ? "active" : ""
                            }`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      className="page-btn prev-next"
                      onClick={() => handlePageChange(getCurrentPage() + 1)}
                      disabled={getCurrentPage() === getTotalPages()}
                    >
                      Next →
                    </button>
                    <button
                      className="page-btn prev-next last-page"
                      onClick={() => handlePageChange(getTotalPages())}
                      disabled={getCurrentPage() === getTotalPages()}
                      title="Last Page"
                    >
                      »
                    </button>
                  </div>

                  <div className="page-jump">
                    <span>Go to page: </span>
                    <input
                      type="number"
                      min="1"
                      max={getTotalPages()}
                      defaultValue={getCurrentPage()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const page = parseInt(e.target.value);
                          if (page >= 1 && page <= getTotalPages()) {
                            handlePageChange(page);
                          }
                        }
                      }}
                      className="page-input"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReferralHistory;
